import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import Magnetic from "./Magnetic";
import { scrollToId } from "@/lib/scroll";

const ProductVisual = () => {
  const [imgOk, setImgOk] = useState(true);
  if (imgOk) {
    return (
      <div className="relative w-full h-full rounded-[32px] overflow-hidden">
        <img
          data-testid="product-packshot"
          src="/assets/bacio-packshot.webp"
          alt="Skinfill Bacio packaging — box and pre-filled syringe"
          onError={() => setImgOk(false)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-1/4 h-full bg-white/25 blur-md animate-sheen" />
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="product-packshot-placeholder"
      className="relative w-full h-full rounded-[32px] overflow-hidden border border-[#EADECE] bg-gradient-to-br from-[#FCE7EE] via-[#F4EBE6] to-[#E893AE]/40 flex flex-col items-center justify-center text-center p-8"
    >
      <p className="font-serif text-3xl sm:text-4xl text-[#1C1417]">
        SKINFILL <span className="italic text-[#A31D4F]">BACIO</span>
        <sup className="text-xs">®</sup>
      </p>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A31D4F] mt-4">
        Product packshot placeholder
      </p>
    </div>
  );
};

const ProductHero = () => {
  const ref = useRef(null);
  const tiltRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yProduct = useTransform(scrollYProgress, [0, 1], [70, -70]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [40, -40]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(my, { stiffness: 120, damping: 16 });
  const rotateY = useSpring(mx, { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    if (!window.matchMedia("(pointer: fine)").matches || !tiltRef.current) return;
    const r = tiltRef.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="product" ref={ref} data-testid="product-hero-section" className="relative bg-[#FAF7F5] py-24 sm:py-32 lg:py-40 overflow-hidden">
      <motion.div
        style={{ y: yGlow }}
        animate={{ opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[15%] left-[8%] w-[46vw] h-[46vw] max-w-[640px] max-h-[640px] rounded-full bg-[#D84A75]/15 blur-[130px] pointer-events-none"
      />
      <motion.div
        animate={{ opacity: [0.35, 0.6, 0.35], scale: [1, 1.08, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[5%] right-[5%] w-[34vw] h-[34vw] max-w-[480px] max-h-[480px] rounded-full bg-[#D4AF37]/10 blur-[120px] pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-6 items-center">
        <motion.div
          data-testid="floating-product-card"
          style={{ y: yProduct }}
          className="lg:col-span-7 relative mx-auto w-full max-w-[580px]"
        >
          <div className="absolute -top-8 -left-6 w-36 h-36 rounded-full glass-panel opacity-70 pointer-events-none hidden sm:block" />
          <div className="absolute -bottom-10 -right-4 w-52 h-52 rounded-[40px] glass-panel opacity-50 rotate-12 pointer-events-none hidden sm:block" />
          <motion.div
            ref={tiltRef}
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
            style={{ rotateX, rotateY, transformPerspective: 1100 }}
            className="relative aspect-[4/5] animate-float-soft will-change-transform"
          >
            <ProductVisual />
            <div className="absolute -inset-8 rounded-[48px] bg-[#A31D4F]/10 blur-3xl -z-10" />
          </motion.div>
          <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 w-3/4 h-9 bg-[#1C1417]/15 blur-2xl rounded-full" />
        </motion.div>

        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>The icon</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-serif font-light text-[#1C1417] text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.04] tracking-tight mt-6 text-balance">
              ONE PRODUCT.
              <br />
              ONE PURPOSE.
              <br />
              <span className="italic text-[#A31D4F]">BEAUTIFUL LIPS.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 text-base sm:text-lg font-light text-[#6E6266] leading-relaxed max-w-md">
              Designed specifically for the lips — hydration and revitalisation in one beauty concept, for
              refreshed, beautifully conditioned lips. Never an artificial, overfilled look.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-6 font-serif italic text-xl sm:text-2xl text-[#D84A75]">The new era of lip beauty.</p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Hyaluronic Acid 20 mg/mL", "Vitamin B12", "Made in Italy"].map((chip) => (
                <span
                  key={chip}
                  className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#1C1417] border border-[#EADECE] bg-white/70 rounded-full px-4 py-2"
                >
                  {chip}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.34}>
            <div className="mt-10">
              <Magnetic>
                <button
                  data-testid="product-cta-enquiry"
                  onClick={() => scrollToId("#contact")}
                  className="btn-lux btn-lux-magenta"
                >
                  <span>Professional Enquiry</span>
                </button>
              </Magnetic>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ProductHero;
